import {
  Component,
  OnInit,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  NgModule,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TixAnalyticsService } from '@tix/shared/analytics';
import {
  TixButtonModule,
  TixCardModule,
  TixConfirmDialogService,
  TixDialogModule,
  TixIconModule,
  TixInputModule
} from '@tix/shared/ui/components';
import { Maybe } from 'graphql/jsutils/Maybe';
import { TixBusinessCodeGroup, TixWarning } from '@tix/data-access';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { TextMaskModule } from 'angular2-text-mask';
import { BtnLoadingDirectiveModule } from '../btn-loading/btn-loading.directive';
import { TixBusinessGroup } from '@tix/shared/state';
import { TixFullVenue } from '@tix/venues/state';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';

type VenueWarning = {
  companyId: null;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
  venueId: string;
  venueWarningId: string;
  warning: {
    createAt: string;
    message: string;
    type: string;
    updatedAt: string;
    updatedBy: string;
  };
  warningId: string;
  __typename: 'VenueWarning';
};
@Component({
  selector: 'tix-warning',
  templateUrl: './warning.component.html',
  styleUrls: ['./warning.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WarningComponent implements OnInit {
  @Input() pageName: string = '';

  constructor(
    private confirmDialogService: TixConfirmDialogService,
    private cdRef: ChangeDetectorRef,
    private analytics: TixAnalyticsService
  ) {}

  @Input() warnings!:
    | Maybe<TixWarning>[]
    | TixFullVenue['warnings']
    | null
    | undefined;
  @Input() warningBusinessCodeGroup: Maybe<TixBusinessGroup>;
  @Input() venueWarnings = false;
  @Output() save: EventEmitter<Partial<TixWarning>[]> = new EventEmitter();
  @Output() delete = new EventEmitter<TixWarning>();

  @Input() keepDisableSaveBtn = false;

  @Input() isLoading: Maybe<boolean>;
  @Input() showHeader: boolean = true;

  @Input() canAdd?: boolean | null = true;
  @Input() canEdit?: boolean | null = true;
  @Input() canDelete?: boolean | null = true;

  warningForm: FormGroup;

  get warningsFormArray() {
    return this.warningForm.get('warnings') as FormArray;
  }

  get warningTypes() {
    if (this.warningBusinessCodeGroup) {
      return this.warningBusinessCodeGroup.businessCodes;
    }
    return [];
  }

  //
  // get socialMediaValueFormatted() {
  //   return (this.warningsFormArray.value as WarningValue[]).map(
  //     formGroup =>
  //       ({
  //         socialMediaId: formGroup.socialMediaId,
  //         socialMediaType: formGroup.socialMediaType,
  //         url: formGroup.socialMediaUrl
  //       } as Partial<TixSocialMedia>)
  //   );
  // }

  ngOnInit(): void {
    this.warningForm = setupWarningsForm(
      this.venueWarnings,
      (this.warnings || []) as TixWarning[]
    );

    if (!this.canEdit) this.warningForm.disable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const warnings: Maybe<Array<Maybe<TixWarning>>> = changes['warnings']
      ?.currentValue as Maybe<TixWarning[]>;
    if (warnings) {
      this.warningForm = setupWarningsForm(this.venueWarnings, warnings);

      if (!this.canEdit) this.warningForm.disable();
    }
  }

  addNewWarning() {
    const prefix =
      this.pageName === 'venueDetails'
        ? 'venue'
        : this.pageName === 'companyDetails'
        ? 'company'
        : 'generic';
    this.analytics.captureEvent(`${prefix}_warning_add_clicked`);
    this.warningsFormArray.push(createWarningFormGroup());
  }

  handleSaveClick() {
    const prefix =
      this.pageName === 'venueDetails'
        ? 'venue'
        : this.pageName === 'companyDetails'
        ? 'company'
        : 'generic';
    this.analytics.captureEvent(`${prefix}_warnings_saved`);
    const warningsFormValue = this.warningForm.value as {
      warnings: WarningValue[];
    };

    if (this.venueWarnings) {
      console.log(warningsFormValue.warnings);
      this.save.emit(warningsFormValue.warnings.map(formatWarningValue));
    } else {
      // Send only the dirty values
      warningsFormValue.warnings = warningsFormValue.warnings.filter(
        (_e, index) => {
          if (this.warningsFormArray.at(index).dirty) {
            return true;
          }
          return false;
        }
      );

      const formattedWarnings =
        warningsFormValue.warnings.map(formatWarningValue);

      this.save.emit(formattedWarnings);
    }
  }

  async confirmDelete(formGroup: AbstractControl, index: number) {
    const prefix =
      this.pageName === 'venueDetails'
        ? 'venue'
        : this.pageName === 'companyDetails'
        ? 'company'
        : 'generic';
    this.analytics.captureEvent(`${prefix}_warning_delete_clicked`);
    if (!this.venueWarnings) {
      const warningId = formGroup.get('warningId')?.value;
      if (warningId) {
        const warning = (this.warnings as any)?.find(
          (warning: TixWarning) => warning?.warningId === warningId
        );
        if (!warning) {
          return;
        }
        const confirmText = warning
          ? `Are you sure you want to delete the warning "${warning.message}" ?`
          : `Are you sure you want to delete this warning?`;
        const result = await this.confirmDialogService.promptConfirm({
          confirmText,
          title: 'Confirm Delete',
          icon: 'exclamation',
          titleTheme: 'danger',
          cancelButton: {
            theme: 'danger'
          },
          confirmButton: {
            theme: 'danger'
          }
        });

        if (result) {
          this.warningsFormArray.removeAt(index);
          this.delete.emit(warning);
        }
        return;
      } else {
        this.warningsFormArray.removeAt(index);
      }
    } else {
      this.warningsFormArray.removeAt(index);
      this.warningsFormArray.markAsDirty();
      if (this.warningsFormArray.length === 0) {
        this.save.emit([]);
        this.addNewWarning();
      }
    }
  }
}

// ----------------------------------------------------------------------------------- #
// ---------------------------------  # functions #  --------------------------------- #
// ----------------------------------------------------------------------------------- #

export type WarningValue = {
  warningMessage: any;
  warningType: any;
  warningId: any;
};

function formatWarningValue(warning: WarningValue) {
  return {
    message: warning.warningMessage,
    warningId: warning.warningId,
    type: warning.warningType
  } as TixWarning;
}

function setupWarningsForm(
  venueWarning: boolean,
  warnings: Array<Maybe<TixWarning>>
) {
  const warningsForm = new FormGroup({
    warnings: createWarningArray(venueWarning, warnings)
  });

  if (warnings.length > 0) {
    (warningsForm.get('warnings') as FormArray).clear();
    warnings.forEach(warning => {
      (warningsForm.get('warnings') as FormArray).push(
        createWarningFormGroup(venueWarning, warning)
      );
    });
  }

  return warningsForm;
}

function createWarningArray(
  venueWarning: boolean,
  warnings: Array<Maybe<TixWarning>>
) {
  if (!warnings || warnings.length === 0)
    return new FormArray([createWarningFormGroup()]);
  return new FormArray(
    warnings.map(warning => createWarningFormGroup(venueWarning, warning))
  );
}

function createWarningFormGroup(
  venueWarning?: boolean,
  warning?: Maybe<TixWarning> | VenueWarning
): FormGroup {
  console.log(warning);
  if (!venueWarning) {
    const currentWarning = warning as Maybe<TixWarning>;
    return new FormGroup({
      warningMessage: new FormControl(currentWarning?.message ?? '', [
        Validators.required
      ]),
      warningType: new FormControl(currentWarning?.type ?? '', [
        Validators.required
      ]),
      warningId: new FormControl(warning?.warningId ?? '')
    } as WarningValue);
  }
  const currentWarning = warning as VenueWarning;
  return new FormGroup({
    warningMessage: new FormControl(currentWarning?.warning.message ?? '', [
      Validators.required
    ]),
    warningType: new FormControl(currentWarning?.warning.type ?? '', [
      Validators.required
    ]),
    warningId: new FormControl(warning?.warningId ?? '')
  } as WarningValue);
}

@NgModule({
  imports: [
    CommonModule,
    TixCardModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    TixInputModule,
    TixIconModule,
    TixButtonModule,
    MatSelectModule,
    TextMaskModule,
    TixDialogModule,
    BtnLoadingDirectiveModule,
    MatButtonModule
  ],
  declarations: [WarningComponent],
  exports: [WarningComponent]
})
export class WarningComponentModule {}
